import referenceMetadataToTitle from '@/analytics/utils/reference-metadata-to-title'
import type { AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'
import { AgentReferenceType, type ClientAgentReference } from '@/agent/references/store/schemas'
import { RiaSourceTriggerLocation, RiaViewerTab, RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'

function normalizeRiaViewerTab(tab: unknown): RiaViewerTab | null {
  switch (tab) {
    case RiaViewerTab.SourceView:
    case 'source':
    case 'Source':
      return RiaViewerTab.SourceView
    case RiaViewerTab.Citator:
    case 'citator':
    case 'Citator':
      return RiaViewerTab.Citator
    default:
      return null
  }
}

const RIA_REFERENCE_TYPES = new Set(['caselaw', 'caselaw_v2', 'caselaw_v3', 'lrr', 'lrr_v2', 'lrr_v3', 'user_file'])

export type RiaSourceContext = {
  conversationId: string
  messageId: string
  matterId?: string
}

function normalizeOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function toLower(value: unknown): string | null {
  return typeof value === 'string' ? value.toLowerCase() : null
}

export function createRiaContext(conversationId: unknown, messageId: unknown, matterId?: unknown): RiaSourceContext | null {
  const normalizedConversationId = normalizeOptionalString(conversationId)
  const normalizedMessageId = normalizeOptionalString(messageId)

  if (!normalizedConversationId || !normalizedMessageId) {
    return null
  }

  return {
    conversationId: normalizedConversationId,
    messageId: normalizedMessageId,
    matterId: normalizeOptionalString(matterId),
  }
}

function inferRiaSourceType(reference: AnalyticsReference): string {
  const metadata = (reference?.metadata ?? {}) as Record<string, unknown>
  const normalizedType = toLower(metadata.reference_type)

  if (normalizedType && RIA_REFERENCE_TYPES.has(normalizedType)) {
    if (normalizedType === 'user_file') {
      return 'user_file'
    }
    if (normalizedType.startsWith('caselaw')) {
      return 'caselaw'
    }
    if (normalizedType.startsWith('lrr')) {
      return 'lrr'
    }
    return normalizedType
  }

  if (typeof metadata.user_file_id === 'string' && metadata.user_file_id.length > 0) {
    return 'user_file'
  }

  if (typeof metadata.file_reference_user_file_id === 'string' && metadata.file_reference_user_file_id.length > 0) {
    return 'user_file'
  }

  if (typeof metadata.parent_id === 'string' && metadata.parent_id.length > 0) {
    return 'caselaw'
  }

  if (typeof metadata.case_id === 'string' && metadata.case_id.length > 0) {
    return 'caselaw'
  }

  if (typeof metadata.slug === 'string' && metadata.slug.length > 0) {
    return 'lrr'
  }

  if (typeof metadata.law_type === 'string' && metadata.law_type.length > 0) {
    return 'lrr'
  }

  if (typeof metadata.citation === 'string' && typeof metadata.jurisdiction_code === 'string') {
    return 'caselaw'
  }

  if (typeof metadata.file_path === 'string' && metadata.file_path.length > 0) {
    return 'lrr'
  }

  return 'unknown'
}

function resolveRiaSourceCitationId(reference: AnalyticsReference): string | null {
  const metadata = (reference?.metadata ?? {}) as Record<string, unknown>

  const citationIdCandidates = [
    metadata.citation_id,
    metadata.citationId,
    metadata.reference_id,
    metadata.referenceId,
    metadata.parent_id,
    metadata.parentId,
    reference?.reference_number,
  ]

  for (const candidate of citationIdCandidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate
    }
  }

  return null
}

type CreateRiaSourceViewerOpenedPayloadParams = {
  context: RiaSourceContext
  reference: AnalyticsReference
  triggerLocation: RiaSourceTriggerLocation
}

function createRiaSourceViewerOpenedPayload(params: CreateRiaSourceViewerOpenedPayloadParams): {
  conversationId: string
  riaMessageId: string
  riaSourceCitationId: string
  riaSourceType: string
  riaSourceTriggerLocation: RiaSourceTriggerLocation
  riaSourceTitle?: string
  matterId?: string
} | null {
  const { context, reference, triggerLocation } = params
  const { conversationId, messageId, matterId } = context

  const citationId = resolveRiaSourceCitationId(reference)
  if (!citationId) {
    return null
  }

  const sourceType = inferRiaSourceType(reference)
  const sourceTitle = referenceMetadataToTitle(reference)

  const payload: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  } = {
    conversationId,
    riaMessageId: messageId,
    riaSourceCitationId: citationId,
    riaSourceType: sourceType,
    riaSourceTriggerLocation: triggerLocation,
  }

  if (typeof sourceTitle === 'string') {
    const trimmedTitle = sourceTitle.trim()
    if (trimmedTitle.length > 0 && trimmedTitle.toLowerCase() !== 'source') {
      payload.riaSourceTitle = trimmedTitle
    }
  }

  const normalizedMatterId = normalizeOptionalString(matterId)
  if (normalizedMatterId) {
    payload.matterId = normalizedMatterId
  }

  return payload
}

type TrackRiaSourceViewerOpenedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext
  reference: AnalyticsReference
  triggerLocation: RiaSourceTriggerLocation
  warn?: (message: string, context?: unknown) => void
}

type RiaSourcePayload = NonNullable<ReturnType<typeof createRiaSourceViewerOpenedPayload>>
type RiaSourceDownloadPayload = Omit<RiaSourcePayload, 'riaSourceTitle'>
type RiaSourceViewerTabChangedPayload = {
  conversationId: string
  riaMessageId: string
  fromTab: RiaViewerTab
  toTab: RiaViewerTab
  matterId?: string
}
type RiaCaseAnalysisRequestedPayload = RiaSourcePayload
type RiaCitatorSectionExpandedPayload = {
  conversationId: string
  riaMessageId: string
  riaCitatorSection: RiaCitatorSection
  riaCitatorSectionCount?: number
  matterId?: string
}
type RiaCitatorCaseSelectedPayload = {
  conversationId: string
  riaMessageId: string
  riaCitatorSection: RiaCitatorSection
  riaCitatorCaseId: string
  riaCitatorCaseTitle?: string
  matterId?: string
}
type RiaCitatorRefreshClickedPayload = {
  conversationId: string
  riaMessageId: string
  riaCitatorSection?: RiaCitatorSection
  riaCitatorRefreshReason?: string
  matterId?: string
}

export function trackRiaSourceViewerOpened(params: TrackRiaSourceViewerOpenedParams): ReturnType<typeof createRiaSourceViewerOpenedPayload> {
  const { trackEvent, warn, ...payloadParams } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaSourceViewerOpened')
    return null
  }

  const payload = createRiaSourceViewerOpenedPayload(payloadParams)
  if (!payload) {
    warn?.('[RIA Analytics] Unable to track RiaSourceViewerOpened because payload could not be built', payloadParams)
    return null
  }

  trackEvent(AnalyticsEvent.RiaSourceViewerOpened, payload)
  return payload
}

export function trackRiaSourceDownload(params: TrackRiaSourceViewerOpenedParams): RiaSourceDownloadPayload | null {
  const { trackEvent, warn, ...payloadParams } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaSourceDownloadClicked')
    return null
  }

  const payload = createRiaSourceViewerOpenedPayload(payloadParams)
  if (!payload) {
    warn?.('[RIA Analytics] Unable to track RiaSourceDownloadClicked because payload could not be built', payloadParams)
    return null
  }

  const { riaSourceTitle: _omit, ...downloadPayload } = payload

  trackEvent(AnalyticsEvent.RiaSourceDownloadClicked, downloadPayload)
  return downloadPayload
}

export function trackRiaCaseAnalysisRequested(params: TrackRiaSourceViewerOpenedParams): RiaCaseAnalysisRequestedPayload | null {
  const { trackEvent, warn, ...payloadParams } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaCaseAnalysisRequested')
    return null
  }

  const payload = createRiaSourceViewerOpenedPayload(payloadParams)
  if (!payload) {
    warn?.('[RIA Analytics] Unable to track RiaCaseAnalysisRequested because payload could not be built', payloadParams)
    return null
  }

  trackEvent(AnalyticsEvent.RiaCaseAnalysisRequested, payload)
  return payload
}

type TrackRiaSourceViewerTabChangedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext | null | undefined
  fromTab: RiaViewerTab | string | null | undefined
  toTab: RiaViewerTab | string | null | undefined
  warn?: (message: string, context?: unknown) => void
}

export function trackRiaSourceViewerTabChanged(params: TrackRiaSourceViewerTabChangedParams): RiaSourceViewerTabChangedPayload | null {
  const { trackEvent, context, fromTab, toTab, warn } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaSourceViewerTabChanged')
    return null
  }
  if (!context) {
    warn?.('[RIA Analytics] No context available for RiaSourceViewerTabChanged')
    return null
  }
  const normalizedFromTab = normalizeRiaViewerTab(fromTab)
  const normalizedToTab = normalizeRiaViewerTab(toTab)
  if (!normalizedFromTab || !normalizedToTab || normalizedFromTab === normalizedToTab) {
    return null
  }

  const payload: RiaSourceViewerTabChangedPayload = {
    conversationId: context.conversationId,
    riaMessageId: context.messageId,
    fromTab: normalizedFromTab,
    toTab: normalizedToTab,
  }

  if (context.matterId) {
    payload.matterId = context.matterId
  }

  trackEvent(AnalyticsEvent.RiaSourceViewerTabChanged, payload)
  return payload
}

export function normalizeRiaCitatorSection(section: RiaCitatorSection | string | null | undefined): RiaCitatorSection | null {
  if (typeof section !== 'string') {
    return null
  }
  const lowerSection = section.toLowerCase().trim()

  // Map title strings to enum values
  if (lowerSection === 'important cases' || lowerSection === RiaCitatorSection.ImportantCases) {
    return RiaCitatorSection.ImportantCases
  }
  if (lowerSection === 'case citations' || lowerSection === RiaCitatorSection.CaseCitations) {
    return RiaCitatorSection.CaseCitations
  }
  if (lowerSection === 'citing cases' || lowerSection === RiaCitatorSection.CitingCases) {
    return RiaCitatorSection.CitingCases
  }
  if (lowerSection === 'related cases' || lowerSection === RiaCitatorSection.RelatedCases) {
    return RiaCitatorSection.RelatedCases
  }

  // Try direct enum value match
  if (Object.values(RiaCitatorSection).includes(lowerSection as RiaCitatorSection)) {
    return lowerSection as RiaCitatorSection
  }

  return null
}

type TrackRiaCitatorSectionExpandedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext | null | undefined
  section: RiaCitatorSection | string | null | undefined
  sectionCount?: number | null | undefined
  warn?: (message: string, context?: unknown) => void
}

export function trackRiaCitatorSectionExpanded(params: TrackRiaCitatorSectionExpandedParams): RiaCitatorSectionExpandedPayload | null {
  const { trackEvent, context, section, sectionCount, warn } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaCitatorSectionExpanded')
    return null
  }
  if (!context) {
    warn?.('[RIA Analytics] No context available for RiaCitatorSectionExpanded')
    return null
  }

  const normalizedSection = normalizeRiaCitatorSection(section)
  if (!normalizedSection) {
    return null
  }

  const payload: RiaCitatorSectionExpandedPayload = {
    conversationId: context.conversationId,
    riaMessageId: context.messageId,
    riaCitatorSection: normalizedSection,
  }

  // Add count if provided and is a valid positive number
  if (typeof sectionCount === 'number' && sectionCount >= 0) {
    payload.riaCitatorSectionCount = sectionCount
  }

  if (context.matterId) {
    payload.matterId = context.matterId
  }

  trackEvent(AnalyticsEvent.RiaCitatorSectionExpanded, payload)
  return payload
}

/**
 * Resolve citator case ID from item metadata
 * Priority: parent_id > s3_path
 * Returns null if neither exists
 */
type ResolveCitatorCaseIdParams = {
  parentId?: string | null | undefined
  s3Path?: string | null | undefined
}

export function resolveCitatorCaseId(params: ResolveCitatorCaseIdParams): string | null {
  const { parentId, s3Path } = params

  // Prefer parent_id over s3_path
  if (typeof parentId === 'string' && parentId.trim().length > 0) {
    return parentId.trim()
  }

  if (typeof s3Path === 'string' && s3Path.trim().length > 0) {
    return s3Path.trim()
  }

  return null
}

type TrackRiaCitatorCaseSelectedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext | null | undefined
  section: RiaCitatorSection | string | null | undefined
  caseId: string | null | undefined
  caseTitle?: string | null | undefined
  warn?: (message: string, context?: unknown) => void
}

export function trackRiaCitatorCaseSelected(params: TrackRiaCitatorCaseSelectedParams): RiaCitatorCaseSelectedPayload | null {
  const { trackEvent, context, section, caseId, caseTitle, warn } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaCitatorCaseSelected')
    return null
  }
  if (!context) {
    warn?.('[RIA Analytics] No context available for RiaCitatorCaseSelected')
    return null
  }

  const normalizedSection = normalizeRiaCitatorSection(section)
  if (!normalizedSection) {
    warn?.('[RIA Analytics] Invalid section for RiaCitatorCaseSelected', section)
    return null
  }

  if (!caseId || typeof caseId !== 'string' || caseId.trim().length === 0) {
    warn?.('[RIA Analytics] No valid case ID provided for RiaCitatorCaseSelected')
    return null
  }

  const payload: RiaCitatorCaseSelectedPayload = {
    conversationId: context.conversationId,
    riaMessageId: context.messageId,
    riaCitatorSection: normalizedSection,
    riaCitatorCaseId: caseId.trim(),
  }

  // Add case title if provided and valid
  if (typeof caseTitle === 'string' && caseTitle.trim().length > 0) {
    payload.riaCitatorCaseTitle = caseTitle.trim()
  }

  if (context.matterId) {
    payload.matterId = context.matterId
  }

  trackEvent(AnalyticsEvent.RiaCitatorCaseSelected, payload)
  return payload
}

type TrackRiaCitatorRefreshClickedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext | null | undefined
  section?: RiaCitatorSection | string | null | undefined
  refreshReason?: string | null | undefined
  warn?: (message: string, context?: unknown) => void
}

export function trackRiaCitatorRefreshClicked(params: TrackRiaCitatorRefreshClickedParams): RiaCitatorRefreshClickedPayload | null {
  const { trackEvent, context, section, refreshReason, warn } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaCitatorRefreshClicked')
    return null
  }
  if (!context) {
    warn?.('[RIA Analytics] No context available for RiaCitatorRefreshClicked')
    return null
  }

  const payload: RiaCitatorRefreshClickedPayload = {
    conversationId: context.conversationId,
    riaMessageId: context.messageId,
  }

  // Add section if provided and valid
  if (section) {
    const normalizedSection = normalizeRiaCitatorSection(section)
    if (normalizedSection) {
      payload.riaCitatorSection = normalizedSection
    }
  }

  // Add refresh reason if provided and valid
  if (typeof refreshReason === 'string' && refreshReason.trim().length > 0) {
    payload.riaCitatorRefreshReason = refreshReason.trim()
  }

  if (context.matterId) {
    payload.matterId = context.matterId
  }

  trackEvent(AnalyticsEvent.RiaCitatorRefreshClicked, payload)
  return payload
}

type TrackRiaSourceOpenInNewTabClickedParams = {
  trackEvent: TrackEventFn | undefined
  context: RiaSourceContext | null | undefined
  reference: AnalyticsReference
  triggerLocation: RiaSourceTriggerLocation
  warn?: (message: string, context?: unknown) => void
}

export function trackRiaSourceOpenInNewTabClicked(
  params: TrackRiaSourceOpenInNewTabClickedParams
): ReturnType<typeof createRiaSourceViewerOpenedPayload> | null {
  const { trackEvent, context, warn, ...payloadParams } = params
  if (!trackEvent) {
    warn?.('[RIA Analytics] trackEvent not available when attempting to track RiaSourceOpenInNewTabClicked')
    return null
  }
  if (!context) {
    warn?.('[RIA Analytics] No context available for RiaSourceOpenInNewTabClicked')
    return null
  }

  // Reuse the same payload creation logic as RiaSourceViewerOpened
  const payload = createRiaSourceViewerOpenedPayload({
    context,
    ...payloadParams,
  })

  if (!payload) {
    return null
  }

  trackEvent(AnalyticsEvent.RiaSourceOpenInNewTabClicked, payload)
  return payload
}

type BuildChatReferenceFromMetadataOptions = {
  referenceNumber?: string
  referenceType?: string
}

function buildChatReferenceFromMetadata(metadataInput: Record<string, unknown>, options: BuildChatReferenceFromMetadataOptions = {}): AnalyticsReference {
  const metadata: Record<string, unknown> = { ...metadataInput }

  if (options.referenceType && typeof metadata.reference_type !== 'string') {
    metadata.reference_type = options.referenceType
  }

  const referenceNumberCandidates: Array<string | undefined> = [
    options.referenceNumber,
    typeof metadata.reference_id === 'string' ? metadata.reference_id : undefined,
    typeof metadata.parent_id === 'string' ? metadata.parent_id : undefined,
    typeof metadata.citation_id === 'string' ? metadata.citation_id : undefined,
  ]

  const referenceNumber = referenceNumberCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0) ?? 'source'

  return {
    reference_number: String(referenceNumber),
    text: '',
    metadata,
    relevant_sentences: null,
    relevant_highlights: null,
  }
}

export function buildChatReferenceFromAgentReference(agentReference: ClientAgentReference): AnalyticsReference {
  const metadata = { ...(agentReference.metadata as unknown as Record<string, unknown>) }
  const referenceNumber = agentReference.reference.citation_id || agentReference.reference.id

  const fileReference = agentReference.type === AgentReferenceType.USER_FILE_QDRANT_CHUNK ? agentReference.file_reference : null
  const isUserFileReference =
    agentReference.type === AgentReferenceType.USER_FILE_QDRANT_CHUNK ||
    agentReference.type === AgentReferenceType.FILE_UPLOAD_CHUNK ||
    agentReference.type === AgentReferenceType.FILE_UPLOAD_FULL ||
    Boolean(fileReference?.user_file_id)

  if (isUserFileReference) {
    metadata.reference_type = 'user_file'
  }

  if (fileReference) {
    metadata.file_reference_user_file_id = fileReference.user_file_id
    metadata.user_file_id = fileReference.user_file_id

    metadata.file_reference_filename = fileReference.filename
    metadata.filename = fileReference.filename
  }

  if (isUserFileReference && typeof metadata.filename !== 'string' && typeof metadata.file_path === 'string') {
    metadata.filename = metadata.file_path.split('/').pop()
  }

  return buildChatReferenceFromMetadata(metadata, {
    referenceNumber: typeof referenceNumber === 'string' ? referenceNumber : String(referenceNumber),
  })
}

export function buildChatReferenceFromStandaloneMetadata(metadataInput: Record<string, unknown>, fallbackReferenceNumber?: string): AnalyticsReference {
  const metadata: Record<string, unknown> = {
    reference_type: 'caselaw',
    ...metadataInput,
  }

  if (typeof metadata.citation_id !== 'string' && typeof metadata.parent_id === 'string') {
    metadata.citation_id = metadata.parent_id
  }

  return buildChatReferenceFromMetadata(metadata, {
    referenceNumber: fallbackReferenceNumber ?? (typeof metadata.parent_id === 'string' ? metadata.parent_id : undefined),
    referenceType: 'caselaw',
  })
}

export function buildRiaContextFromAgentReference(agentReference: ClientAgentReference, matterId?: string | null): RiaSourceContext {
  return (
    createRiaContext(agentReference.conversation_id, agentReference.reference.turn_id, matterId) ?? {
      conversationId: agentReference.conversation_id,
      messageId: agentReference.reference.turn_id,
      matterId: normalizeOptionalString(matterId),
    }
  )
}
